import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Library } from "lucide-react";
import api, { getApiErrorMessage } from "../api";
import PageIntro from "../components/ui/PageIntro";
import Dialog from "../components/common/Dialog";
import FormField, {
  SelectInput,
  TextInput,
  TextArea,
} from "../components/common/FormField";
import EntryEditor from "../components/classifier/EntryEditor";
import ProfileEditor from "../components/classifier/ProfileEditor";
import Proposals from "../components/classifier/Proposals";
import ImportConflict from "../components/classifier/ImportConflict";
import { describeRule } from "../components/classifier/RuleEditor";
import "../styles/classifier.css";

const AREAS = [
  ["Процессы и деятельность", ["process", "activity", "route"]],
  ["Требования и проверки", ["requirement", "method", "parameter", "sampling"]],
  ["Категории, группы и роли", ["category", "group", "role"]],
  ["Атрибуты и технологии", ["attribute", "technology", "condition"]],
  [
    "Правила и ответственность",
    ["rule", "dependency", "condition_dependency", "responsibility", "stage"],
  ],
  [
    "Основания и методика",
    [
      "basis",
      "guidance",
      "consistency",
      "assembly",
      "assessment",
      "finding_status",
      "governance",
      "contract_note",
      "measurement_link",
    ],
  ],
];
const STATES = {
  draft: "Черновик",
  review: "На проверке",
  published: "Опубликована",
  retired: "Выведена из использования",
};
const emptyProfile = () => ({
  profiles: [],
  company_flags: {},
  company_conditions: {},
});
function errorMessage(e) {
  const d = e.response?.data?.detail;
  return typeof d === "object"
    ? `${d.message || "Ошибка проверки"}${d.errors?.[0] ? " · " + d.errors[0].message : ""}`
    : getApiErrorMessage(e);
}
function download(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Issues({ value, onSelect }) {
  return (
    <div className="classifier-stack">
      <p className={value.valid ? "classifier-notice" : "classifier-error"}>
        {value.valid
          ? "Техническая проверка пройдена. Требуется решение владельца классификатора."
          : `Ошибок: ${value.errors.length}. Публикация заблокирована.`}
      </p>
      {value.warnings.map((w, i) => (
        <p className="classifier-notice" key={i}>
          {w.message}
        </p>
      ))}
      <div className="classifier-results">
        {value.errors.map((e, i) => (
          <div className="classifier-row" key={i}>
            <span>
              <strong>{e.id}</strong>
              <small>
                {e.field} · {e.message}
              </small>
            </span>
            {onSelect && e.id && (
              <button
                className="ap-button ap-button--secondary"
                type="button"
                onClick={() => onSelect(e)}
              >
                Открыть запись
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ClassifierPage() {
  const [searchParams] = useSearchParams();
  const [contract, setContract] = useState(null),
    [revisions, setRevisions] = useState([]),
    [rid, setRid] = useState(""),
    [revision, setRevision] = useState(null);
  const [refs, setRefs] = useState({ paths: {}, choices: {} }),
    [caps, setCaps] = useState([]),
    [tab, setTab] = useState(() => ['library','preview','publish','exchange','history','proposals'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'library'),
    [kind, setKind] = useState("requirement");
  const [list, setList] = useState({ entries: [], total: 0, counts: {} }),
    [search, setSearch] = useState(""),
    [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [editor, setEditor] = useState(null),
    [validation, setValidation] = useState(null),
    [usage, setUsage] = useState(null);
  const [newDraft, setNewDraft] = useState(false),
    [draft, setDraft] = useState({ version: "", title: "", base_id: "" });
  const [profile, setProfile] = useState(emptyProfile),
    [companies, setCompanies] = useState([]),
    [result, setResult] = useState(null),
    [compareId, setCompareId] = useState(""),
    [scenarioList, setScenarioList] = useState([]),
    [scenarioName, setScenarioName] = useState("");
  const [importView, setImportView] = useState(null),
    [resolutions, setResolutions] = useState({}),
    [reason, setReason] = useState(""),
    [history, setHistory] = useState([]),
    [generated, setGenerated] = useState(null),
    [showCount, setShowCount] = useState(40);
  const flight = useRef(false),
    file = useRef(null);
  const [selected, setSelected] = useState({}),
    [batchReason, setBatchReason] = useState("");
  const editable =
    revision?.state === "draft" && caps.includes("methodology_edit");
  const run = async (fn) => {
    if (flight.current) return;
    flight.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      return await fn();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      flight.current = false;
      setBusy(false);
    }
  };
  const loadRevisions = async () => {
    const r = (await api.get("/classifier/revisions")).data;
    setRevisions(r);
    return r;
  };
  const refresh = async (id = rid) => {
    if (!id) return;
    const [l, r] = await Promise.all([
      api.get(`/classifier/revisions/${id}/entries`, {
        params: { kind, search, offset, limit: 50 },
      }),
      api.get(`/classifier/revisions/${id}/references`),
    ]);
    setList(l.data);
    setRevision(l.data.revision);
    setRefs(r.data);
    await loadRevisions();
  };
  useEffect(() => {
    let active = true;
    Promise.all([
      api.get("/classifier/contract"),
      api.get("/classifier/revisions"),
      api.get("/qualification/access"),
      api.get("/companies/"),
      api.get("/classifier/scenarios"),
    ])
      .then(([c, r, a, co, s]) => {
        if (!active) return;
        setContract(c.data);
        setRevisions(r.data);
        setRid(String(r.data[0]?.id || ""));
        setCaps(a.data.capabilities);
        setCompanies(Array.isArray(co.data) ? co.data : []);
        setScenarioList(s.data);
      })
      .catch((e) => active && setError(errorMessage(e)));
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    if (!rid) return;
    let active = true;
    setValidation(null);
    setResult(null);
    setGenerated(null);
    setImportView(null);
    setSelected({});
    setList({ entries: [], total: 0, counts: {} });
    setRevision(null);
    api
      .get(`/classifier/revisions/${rid}/references`)
      .then((r) => active && setRefs(r.data))
      .catch((e) => active && setError(errorMessage(e)));
    return () => {
      active = false;
    };
  }, [rid]);
  useEffect(() => {
    if (!rid) return;
    let active = true;
    const timer = setTimeout(
      () =>
        api
          .get(`/classifier/revisions/${rid}/entries`, {
            params: { kind, search, offset, limit: 50 },
          })
          .then((r) => {
            if (active) {
              setList(r.data);
              setRevision(r.data.revision);
            }
          })
          .catch((e) => active && setError(errorMessage(e))),
      150,
    );
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [rid, kind, search, offset]);
  const changeKind = (k) => {
    if (k !== kind)
      setList((current) => ({ ...current, entries: [], total: 0 }));
    setKind(k);
    setOffset(0);
    setSearch("");
    setSelected({});
    setTab("library");
  };
  const saveEntry = (record, why) =>
    run(async () => {
      const r = await api.put(`/classifier/revisions/${rid}/entry`, {
        revision: revision.revision,
        reason: why,
        record,
      });
      setValidation(r.data.validation);
      setEditor(null);
      await refresh();
      setNotice("Изменение сохранено в черновике. Аудиты не изменены.");
    });
  const check = () =>
    run(async () => {
      setValidation(
        (await api.get(`/classifier/revisions/${rid}/validation`)).data,
      );
      setTab("publish");
    });
  const move = (state) =>
    run(async () => {
      const r = await api.post(`/classifier/revisions/${rid}/state`, {
        revision: revision.revision,
        state,
        reason: reason || "Переход редакции через интерфейс",
      });
      setRevision(r.data);
      await loadRevisions();
      setNotice(
        "Состояние редакции обновлено. Существующие аудиты не пересчитывались.",
      );
    });
  const preview = () =>
    run(async () => {
      const r = await api.post(`/classifier/revisions/${rid}/preview`, {
        profile,
        compare_revision_id: compareId ? Number(compareId) : null,
      });
      setResult(r.data);
      setShowCount(40);
      setGenerated(null);
    });
  const importFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    run(async () => {
      const form = new FormData();
      form.append("file", f);
      const r = await api.post(
        `/classifier/revisions/${rid}/import-preview`,
        form,
      );
      setImportView(r.data);
      setResolutions({});
      setTab("exchange");
    });
  };
  const openIssue = async (e) =>
    run(async () => {
      const r = await api.get(`/classifier/revisions/${rid}/entries`, {
        params: { kind: e.kind, search: e.id, limit: 50 },
      });
      const row = r.data.entries.find((v) => v.id === e.id);
      if (row) {
        setKind(e.kind);
        setEditor(row);
      }
    });
  const showUsage = (r) =>
    run(async () => {
      setUsage(
        (
          await api.get(`/classifier/revisions/${rid}/usage`, {
            params: { kind: r.kind, key: r.id },
          })
        ).data,
      );
    });
  return (
    <div className="workspace-page classifier-page">
      <PageIntro
        icon={Library}
        title="Редактор требований"
        eyebrow="Настройка библиотеки"
        description="Требования, правила применимости и редакции. Изменения черновика не меняют уже созданные аудиты."
      />
      <div className="classifier-actions"><Link className="ap-button ap-button--primary" to="/audit-preparation">Подготовить аудит</Link><Link to="/requirements">К библиотеке требований</Link></div>
      {error && (
        <p role="alert" className="classifier-error">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="classifier-notice">
          {notice}
        </p>
      )}
      <section className="classifier-panel classifier-toolbar">
        <FormField label="Редакция библиотеки">
          <SelectInput value={rid} onChange={(e) => setRid(e.target.value)}>
            <option value="">Выберите редакцию</option>
            {revisions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.version} · {r.title} · {STATES[r.state]}
              </option>
            ))}
          </SelectInput>
        </FormField>
        {caps.includes("methodology_edit") && (
          <button
            className="ap-button ap-button--primary"
            disabled={busy}
            onClick={() => {
              setDraft({ version: "", title: "", base_id: rid });
              setNewDraft(true);
            }}
          >
            Новый черновик
          </button>
        )}
        <Link
          className="ap-button ap-button--secondary"
          to="/supplier-settings"
        >
          Другие настройки оценки
        </Link>
      </section>
      {!rid && (
        <section className="classifier-panel">
          <h2>Начните с черновика</h2>
          <p>
            Создайте пустой черновик и загрузите исходную книгу. Импорт сначала
            покажет состав и ошибки. Публикация — отдельное действие.
          </p>
        </section>
      )}
      {rid && contract && (
        <>
          <nav
            className="classifier-tabs"
            aria-label="Работа с классификатором"
          >
            {[
              ["library", "Содержание и связи"],
              ["preview", "Проверка правил на примере"],
              ["publish", "Проверка и публикация"],
              ["exchange", "Excel"],
              ["history", "История"],
              ...(caps.includes("methodology_edit")
                ? [["proposals", "Предложения из аудитов"]]
                : []),
            ].map(([k, label]) => (
              <button
                className="ap-button ap-button--secondary"
                aria-current={tab === k ? "page" : undefined}
                key={k}
                onClick={() => {
                  setTab(k);
                  if (k === "history")
                    run(async () =>
                      setHistory(
                        (await api.get(`/classifier/revisions/${rid}/history`))
                          .data,
                      ),
                    );
                }}
              >
                {label}
              </button>
            ))}
          </nav>
          {tab === "proposals" && revision && (
            <Proposals
              revision={revision}
              contract={contract}
              refs={refs}
              onChanged={refresh}
            />
          )}
          {!revision && (
            <p role="status" className="classifier-notice">
              Загружаем редакцию библиотеки…
            </p>
          )}
          {revision && revision.state !== "draft" && (
            <p className="classifier-notice">
              {STATES[revision?.state]}. Содержание защищено от изменения. Для
              доработки создайте новый черновик на основе этой редакции.
            </p>
          )}
          {tab === "library" && (
            <div className="classifier-library">
              <aside
                className="classifier-panel classifier-navigation"
                aria-label="Области библиотеки"
              >
                {AREAS.map(([title, kinds]) => (
                  <section key={title}>
                    <h2>{title}</h2>
                    {kinds.map((k) => (
                      <button
                        key={k}
                        className="classifier-nav-item"
                        aria-current={kind === k ? "page" : undefined}
                        onClick={() => changeKind(k)}
                      >
                        {contract.tables[k]?.sheet}
                        <span>{list.counts[k] || 0}</span>
                      </button>
                    ))}
                  </section>
                ))}
              </aside>
              <section className="classifier-panel classifier-stack">
                <div className="classifier-toolbar">
                  <h2>{contract.tables[kind]?.sheet}</h2>
                  {editable && (
                    <button
                      className="ap-button ap-button--primary"
                      onClick={() => setEditor({ kind, newRecord: true })}
                    >
                      Добавить запись
                    </button>
                  )}
                </div>
                <FormField label="Поиск по коду и содержанию">
                  <TextInput
                    type="search"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setOffset(0);
                    }}
                  />
                </FormField>
                <p className="classifier-muted">
                  Записей: {list.total}. Изменения относятся только к выбранной
                  редакции.
                </p>
                {editable && (
                  <details className="classifier-disclosure">
                    <summary>Массовое изменение использования</summary>
                    <div className="classifier-stack">
                      <p>
                        Выберите записи текущей страницы. Связанные требования
                        не удаляются; отключённые ссылки будут блокировать
                        публикацию.
                      </p>
                      <div className="classifier-checkboxes">
                        {list.entries.map((r) => (
                          <label key={r.id}>
                            <input
                              type="checkbox"
                              checked={!!selected[r.id]}
                              onChange={(e) =>
                                setSelected({
                                  ...selected,
                                  [r.id]: e.target.checked,
                                })
                              }
                            />
                            {r.id}
                          </label>
                        ))}
                      </div>
                      <FormField label="Обоснование массового изменения">
                        <TextArea
                          value={batchReason}
                          onChange={(e) => setBatchReason(e.target.value)}
                        />
                      </FormField>
                      <div className="classifier-actions">
                        {[
                          ["inactive", "Вывести выбранные из использования"],
                          ["active", "Вернуть выбранные"],
                        ].map(([status, label]) => (
                          <button
                            key={status}
                            className="ap-button ap-button--secondary"
                            disabled={
                              busy ||
                              !batchReason.trim() ||
                              !list.entries.some((r) => selected[r.id])
                            }
                            onClick={() =>
                              run(async () => {
                                const r = await api.post(
                                  `/classifier/revisions/${rid}/entries`,
                                  {
                                    revision: revision.revision,
                                    reason: batchReason,
                                    records: list.entries
                                      .filter((r) => selected[r.id])
                                      .map((r) => ({ ...r, status })),
                                  },
                                );
                                setValidation(r.data.validation);
                                setSelected({});
                                setBatchReason("");
                                await refresh();
                                setNotice(
                                  "Статусы изменены. Проверьте зависимые записи перед публикацией.",
                                );
                              })
                            }
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </details>
                )}
                <div className="classifier-results">
                  {list.entries.map((r) => (
                    <article key={r.id} className="classifier-row">
                      <div>
                        <h3>{r.data.title || r.id}</h3>
                        <small>
                          {r.id} ·{" "}
                          {r.status === "active"
                            ? "Используется"
                            : "Выведена из использования"}
                        </small>
                        {r.data.requirement && <p>{r.data.requirement}</p>}
                        {r.data.condition && (
                          <p className="classifier-muted">
                            {describeRule(r.data.condition, refs.paths)}
                          </p>
                        )}
                      </div>
                      <div className="classifier-actions">
                        <button
                          className="ap-button ap-button--secondary"
                          onClick={() => showUsage(r)}
                        >
                          Где используется
                        </button>
                        {editable && (
                          <>
                            <button
                              className="ap-button ap-button--secondary"
                              onClick={() => setEditor(r)}
                            >
                              Изменить
                            </button>
                            <button
                              className="ap-button ap-button--secondary"
                              onClick={() =>
                                setEditor({
                                  ...JSON.parse(JSON.stringify(r)),
                                  id: "C-" + crypto.randomUUID(),
                                  newRecord: true,
                                })
                              }
                            >
                              Копировать
                            </button>
                          </>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
                <div className="classifier-toolbar">
                  <button
                    className="ap-button ap-button--secondary"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - 50))}
                  >
                    Предыдущие
                  </button>
                  <span>
                    {list.total ? offset + 1 : 0}–
                    {Math.min(offset + 50, list.total)} из {list.total}
                  </span>
                  <button
                    className="ap-button ap-button--secondary"
                    disabled={offset + 50 >= list.total}
                    onClick={() => setOffset(offset + 50)}
                  >
                    Следующие
                  </button>
                </div>
              </section>
            </div>
          )}
          {tab === "preview" && (
            <section className="classifier-panel classifier-stack">
              <h2>Предварительный чек-лист</h2>
              <p>
                Это проверка состава, не оценка организации. Ответы и
                действующие аудиты не изменяются.
              </p>
              <div className="classifier-toolbar">
                <FormField label="Сохранённый проверочный профиль">
                  <SelectInput
                    value=""
                    onChange={(e) => {
                      const s = scenarioList.find(
                        (s) => s.id === Number(e.target.value),
                      );
                      if (s) {
                        setProfile(s.profile);
                        setScenarioName(s.title);
                        setResult(null);
                      }
                    }}
                  >
                    <option value="">Выберите сценарий</option>
                    {scenarioList.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.title}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>
                <FormField label="Название сценария">
                  <TextInput
                    value={scenarioName}
                    onChange={(e) => setScenarioName(e.target.value)}
                  />
                </FormField>
                <button
                  disabled={
                    busy ||
                    !scenarioName.trim() ||
                    !caps.includes("methodology_edit")
                  }
                  className="ap-button ap-button--secondary"
                  onClick={() =>
                    run(async () => {
                      await api.post("/classifier/scenarios", {
                        title: scenarioName,
                        profile,
                        expected: {},
                      });
                      setScenarioList(
                        (await api.get("/classifier/scenarios")).data,
                      );
                      setNotice(
                        "Проверочный профиль сохранён отдельно от библиотеки. В Excel он не попадёт.",
                      );
                    })
                  }
                >
                  Сохранить сценарий
                </button>
              </div>
              <ProfileEditor
                value={profile}
                onChange={(v) => {
                  setProfile(v);
                  setResult(null);
                }}
                refs={refs}
                companies={companies}
              />
              <div className="classifier-toolbar">
                <FormField label="Сравнить с редакцией">
                  <SelectInput
                    value={compareId}
                    onChange={(e) => setCompareId(e.target.value)}
                  >
                    <option value="">Без сравнения</option>
                    {revisions
                      .filter((r) => String(r.id) !== String(rid))
                      .map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.version} · {r.title}
                        </option>
                      ))}
                  </SelectInput>
                </FormField>
                <button
                  disabled={busy}
                  className="ap-button ap-button--primary"
                  onClick={preview}
                >
                  {busy
                    ? "Проверяем…"
                    : "Сформировать предварительный чек-лист"}
                </button>
              </div>
              {result && (
                <>
                  <p className="classifier-notice">
                    {result.recommendation}. Проверок: {result.items.length};
                    уточнений: {result.questions.length}; исключений:{" "}
                    {result.excluded.length}.
                  </p>
                  {!result.validation.valid && (
                    <Issues value={result.validation} />
                  )}
                  <details className="classifier-disclosure">
                    <summary>
                      Вопросы для уточнения ({result.questions.length})
                    </summary>
                    <ul>
                      {result.questions.map((q) => (
                        <li key={q.id}>
                          <strong>{q.profile_id || "Организация"}</strong> ·{" "}
                          {q.text}
                        </li>
                      ))}
                    </ul>
                  </details>
                  <details className="classifier-disclosure">
                    <summary>
                      Значимые исключения ({result.excluded.length})
                    </summary>
                    {result.excluded.map((e, i) => (
                      <p key={i}>
                        {e.requirement_id || e.process_id} ·{" "}
                        {e.profile_id || "Организация"} · {e.reason}
                      </p>
                    ))}
                  </details>
                  {result.comparison && (
                    <details className="classifier-disclosure" open>
                      <summary>
                        Различия: добавлено {result.comparison.added.length},
                        исключено {result.comparison.removed.length}, изменено{" "}
                        {result.comparison.changed.length}
                      </summary>
                      {[
                        ["added", "Добавлено"],
                        ["removed", "Исключено"],
                        ["changed", "Изменено"],
                      ].map(([k, label]) => (
                        <div key={k}>
                          <h3>{label}</h3>
                          {result.comparison[k].slice(0, 60).map((i) => (
                            <p key={(i.after || i).key}>
                              {(i.after || i).requirement_id} ·{" "}
                              {(i.after || i).requirement.title}
                            </p>
                          ))}
                        </div>
                      ))}
                    </details>
                  )}
                  <div className="classifier-results">
                    {result.items.slice(0, showCount).map((i) => (
                      <details key={i.key} className="classifier-disclosure">
                        <summary>
                          {i.requirement.title}{" "}
                          <span className="classifier-muted">
                            {i.status === "potential"
                              ? "Нужно уточнить"
                              : "Включено"}{" "}
                            · {i.context.process} ·{" "}
                            {i.context.site_id || "Организация"}
                          </span>
                        </summary>
                        <div className="classifier-stack">
                          <p>{i.requirement.requirement}</p>
                          <p>
                            <strong>Действия аудитора:</strong>{" "}
                            {i.requirement.actions}
                          </p>
                          <p>
                            <strong>Критерий:</strong> {i.requirement.criterion}
                          </p>
                          <p>
                            <strong>Доказательства:</strong>{" "}
                            {i.requirement.evidence}
                          </p>
                          <p>{i.procedure}</p>
                          <h4>Почему включено</h4>
                          {i.reasons.map((r, j) => (
                            <div key={j}>
                              <p>
                                {describeRule(r.condition, refs.paths)} →{" "}
                                {r.resolved_process} → {r.requirement_id}
                              </p>
                              <dl>
                                {Object.entries(r.inputs).map(([k, v]) => (
                                  <React.Fragment key={k}>
                                    <dt>{refs.paths[k]?.[0]?.title || k}</dt>
                                    <dd>
                                      {v === null ? "Не заполнено" : String(v)}
                                    </dd>
                                  </React.Fragment>
                                ))}
                              </dl>
                            </div>
                          ))}
                          {i.parameters.length > 0 && (
                            <>
                              <h4>Параметры метода</h4>
                              {i.parameters.map((p) => (
                                <p key={p.id}>
                                  {p.title} · {p.units} ·{" "}
                                  {p.applicability === "unknown"
                                    ? "обязательность требует уточнения"
                                    : "обязателен"}
                                </p>
                              ))}
                            </>
                          )}
                        </div>
                      </details>
                    ))}
                  </div>
                  {showCount < result.items.length && (
                    <button
                      className="ap-button ap-button--secondary"
                      onClick={() => setShowCount(showCount + 40)}
                    >
                      Показать ещё проверки
                    </button>
                  )}
                  {revision?.state === "published" &&
                    caps.includes("methodology_publish") && (
                      <button
                        className="ap-button ap-button--primary"
                        disabled={busy || !result.items.length}
                        onClick={() =>
                          run(async () => {
                            setGenerated(
                              (
                                await api.post(
                                  `/classifier/revisions/${rid}/checklists`,
                                  {
                                    profile,
                                    name: `Чек-лист организации · ${revision.version}`,
                                    allow_incomplete: !result.complete,
                                    request_key: crypto.randomUUID(),
                                  },
                                )
                              ).data,
                            );
                          })
                        }
                      >
                        {result.complete
                          ? "Создать чек-лист для нового аудита"
                          : "Создать предварительный чек-лист с открытыми уточнениями"}
                      </button>
                    )}
                  {generated && (
                    <Link
                      className="ap-button ap-button--primary"
                      to={`/audits/new?template_id=${generated.template_id}&audited_company_id=${profile.company_id}`}
                    >
                      Перейти к созданию аудита
                    </Link>
                  )}
                </>
              )}
            </section>
          )}
          {tab === "publish" && (
            <section className="classifier-panel classifier-stack">
              <h2>Проверка и публикация</h2>
              <p>
                Опубликованная редакция неизменяема. Публикация не перестраивает
                текущие аудиты. Для переноса изменений в аудит потребуется
                отдельное сравнение.
              </p>
              <button
                disabled={busy}
                className="ap-button ap-button--secondary"
                onClick={check}
              >
                Проверить библиотеку
              </button>
              {validation && (
                <Issues
                  value={validation}
                  onSelect={editable ? openIssue : null}
                />
              )}
              <FormField label="Основание решения">
                <TextArea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </FormField>
              <div className="classifier-actions">
                {revision?.state === "draft" &&
                  caps.includes("methodology_edit") && (
                    <button
                      className="ap-button ap-button--primary"
                      disabled={busy || !reason.trim()}
                      onClick={() => move("review")}
                    >
                      Передать на проверку
                    </button>
                  )}
                {revision?.state === "review" && (
                  <>
                    <button
                      className="ap-button ap-button--secondary"
                      disabled={busy || !reason.trim()}
                      onClick={() => move("draft")}
                    >
                      Вернуть в черновик
                    </button>
                    {caps.includes("methodology_publish") && (
                      <button
                        className="ap-button ap-button--primary"
                        disabled={busy || !reason.trim()}
                        onClick={() => move("published")}
                      >
                        Опубликовать редакцию
                      </button>
                    )}
                  </>
                )}
                {revision?.state === "published" &&
                  caps.includes("methodology_publish") && (
                    <button
                      className="ap-button ap-button--secondary"
                      disabled={busy || !reason.trim()}
                      onClick={() => move("retired")}
                    >
                      Вывести редакцию из использования
                    </button>
                  )}
                {revision?.state === "retired" &&
                  caps.includes("methodology_publish") && (
                    <button
                      className="ap-button ap-button--secondary"
                      disabled={busy || !reason.trim()}
                      onClick={() => move("published")}
                    >
                      Вернуть для новых сборок
                    </button>
                  )}
              </div>
            </section>
          )}
          {tab === "exchange" && (
            <section className="classifier-panel classifier-stack">
              <h2>Обмен Excel</h2>
              <p>
                Редактирование в Excel использует ту же библиотеку и правила.
                Отсутствующие строки не удаляются. Импорт изменяет только
                черновик после подтверждения.
              </p>
              <div className="classifier-actions">
                <button
                  className="ap-button ap-button--secondary"
                  disabled={busy}
                  onClick={() =>
                    run(async () =>
                      download(
                        (
                          await api.get(`/classifier/revisions/${rid}/export`, {
                            responseType: "blob",
                          })
                        ).data,
                        `Классификатор-${revision?.version}.xlsx`,
                      ),
                    )
                  }
                >
                  Выгрузить библиотеку
                </button>
                {editable && (
                  <button
                    className="ap-button ap-button--primary"
                    disabled={busy}
                    onClick={() => file.current.click()}
                  >
                    Загрузить для сравнения
                  </button>
                )}
                <input
                  type="file"
                  ref={file}
                  hidden
                  accept=".xlsx"
                  onChange={importFile}
                />
              </div>
              {importView && (
                <>
                  <p className="classifier-notice">
                    Добавлено: {importView.comparison.counts.added}; изменено:{" "}
                    {importView.comparison.counts.changed}; выводится:{" "}
                    {importView.comparison.counts.retired}; отсутствуют в файле
                    и будут сохранены: {importView.comparison.omitted.length}.
                  </p>
                  <Issues value={importView.validation} />
                  <details className="classifier-disclosure">
                    <summary>Изменения содержания и связей</summary>
                    {importView.comparison.changes.map((c, i) => (
                      <p key={i}>
                        {c.kind} · {c.id} · {c.operation} ·{" "}
                        {c.fields.join(", ")}
                      </p>
                    ))}
                  </details>
                  {importView.conflicts.map((c) => (
                    <ImportConflict
                      key={c.key}
                      conflict={c}
                      spec={contract.tables[c.imported.kind]}
                      paths={refs.paths}
                      value={resolutions[c.key]}
                      onChange={(value) =>
                        setResolutions({ ...resolutions, [c.key]: value })
                      }
                    />
                  ))}
                  <FormField label="Причина импорта">
                    <TextArea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                  </FormField>
                  <button
                    className="ap-button ap-button--primary"
                    disabled={
                      busy ||
                      !reason.trim() ||
                      importView.conflicts.some((c) => !resolutions[c.key])
                    }
                    onClick={() =>
                      run(async () => {
                        const r = await api.post(
                          `/classifier/imports/${importView.id}/apply`,
                          {
                            revision: importView.revision,
                            reason,
                            resolutions,
                          },
                        );
                        setValidation(r.data.validation);
                        setImportView(null);
                        await refresh();
                        setNotice(
                          "Импорт сохранён атомарно в черновике. Ошибки требуют исправления до публикации.",
                        );
                      })
                    }
                  >
                    Применить в черновик
                  </button>
                </>
              )}
            </section>
          )}
          {tab === "history" && (
            <section className="classifier-panel classifier-stack">
              <h2>Кто и что изменил</h2>
              {history.map((h) => (
                <details className="classifier-disclosure" key={h.id}>
                  <summary>
                    {new Intl.DateTimeFormat("ru", {
                      dateStyle: "short",
                      timeStyle: "short",
                      timeZone: "Europe/Moscow",
                    }).format(
                      new Date(
                        h.created_at.endsWith("Z")
                          ? h.created_at
                          : h.created_at + "Z",
                      ),
                    )}{" "}
                    МСК · {h.action} · пользователь {h.actor_id}
                  </summary>
                  <p>{h.payload?.reason}</p>
                  {h.details_truncated && <div className="classifier-stack"><p>Большой импорт показан кратко. Полная история сохранена.</p><button className="ap-button ap-button--secondary" disabled={busy} onClick={() => run(async () => download((await api.get(`/classifier/revisions/${rid}/history/${h.id}/download`, { responseType: "blob" })).data, `classifier-event-${h.id}.json`))}>Скачать полные данные события</button></div>}
                  {h.payload?.changes?.map((c, i) => (
                    <p key={i}>
                      {c.after.kind} · {c.after.id} ·{" "}
                      {c.before ? "изменено" : "добавлено"}
                    </p>
                  ))}
                </details>
              ))}
            </section>
          )}
        </>
      )}
      {editor && contract && (
        <EntryEditor
          key={editor.id || "new"}
          entry={
            editor.newRecord && !editor.data
              ? null
              : (() => {
                  const { newRecord, ...r } = editor;
                  return r;
                })()
          }
          kind={editor.kind || kind}
          spec={contract.tables[editor.kind || kind]}
          refs={refs}
          busy={busy}
          error={error}
          onClose={() => {
            setEditor(null);
            setError("");
          }}
          onSave={saveEntry}
        />
      )}
      {usage && (
        <Dialog
          label="Где используется"
          size="lg"
          className="classifier-dialog"
          onClose={() => setUsage(null)}
        >
          <div className="classifier-dialog-header">
            <h2 data-dialog-heading tabIndex={-1}>
              {usage.entity?.data.title || usage.entity?.id}
            </h2>
            <button
              className="ap-button ap-button--secondary"
              onClick={() => setUsage(null)}
            >
              Закрыть
            </button>
          </div>
          <h3>Используется в ({usage.used_by.length})</h3>
          <div className="classifier-results">
            {usage.used_by.map((r) => (
              <p key={r.kind + ":" + r.id}>
                {r.title} · {r.kind} · {r.id} · {r.fields.join(", ")}
              </p>
            ))}
          </div>
          <h3>Ссылается на</h3>
          {usage.references.map((r, i) => (
            <p key={i}>
              {r.kind} · {r.id} · {r.field}
            </p>
          ))}
          <p className="classifier-notice">
            Для проверки влияния на состав используйте этот же профиль в разделе
            «Проверка сборки» и сравните редакции.
          </p>
        </Dialog>
      )}
      {newDraft && (
        <Dialog
          label="Новый черновик"
          className="classifier-dialog"
          onClose={() => setNewDraft(false)}
        >
          <form
            className="classifier-stack"
            onSubmit={(e) => {
              e.preventDefault();
              run(async () => {
                const r = await api.post("/classifier/revisions", {
                  ...draft,
                  base_id: draft.base_id ? Number(draft.base_id) : null,
                });
                await loadRevisions();
                setRid(String(r.data.id));
                setRevision(r.data);
                setNewDraft(false);
              });
            }}
          >
            <h2 data-dialog-heading tabIndex={-1}>
              Новая редакция
            </h2>
            <FormField label="Обозначение редакции" required>
              <TextInput
                value={draft.version}
                onChange={(e) =>
                  setDraft({ ...draft, version: e.target.value })
                }
                required
              />
            </FormField>
            <FormField label="Название редакции" required>
              <TextInput
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                required
              />
            </FormField>
            <FormField label="На основе">
              <SelectInput
                value={draft.base_id}
                onChange={(e) =>
                  setDraft({ ...draft, base_id: e.target.value })
                }
              >
                <option value="">Пустой черновик</option>
                {revisions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.version} · {r.title}
                  </option>
                ))}
              </SelectInput>
            </FormField>
            {error && (
              <p role="alert" className="classifier-error">
                {error}
              </p>
            )}
            <div className="classifier-dialog-footer">
              <button
                disabled={busy}
                type="submit"
                className="ap-button ap-button--primary"
              >
                Создать черновик
              </button>
              <button
                disabled={busy}
                type="button"
                className="ap-button ap-button--secondary"
                onClick={() => setNewDraft(false)}
              >
                Отмена
              </button>
            </div>
          </form>
        </Dialog>
      )}
    </div>
  );
}
